/* eslint-disable react/no-multi-comp */
import React, { useCallback, useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import { OpenInNew as OpenInNewIcon, PushPin as PinIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { Alert, Box, Button, Chip, IconButton, Paper, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridPinnedRowsProp,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarProps,
  GridToolbarQuickFilter,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import { DEFAULT_PAGE_SIZE } from '~/consts/luxLoyalty';

import {
  type ManualAccrual,
  type ManualAccrualFilterParams,
  type ManualAccrualStatus,
  type ProductType,
  getManualAccruals,
} from '~/services/LuxLoyalty';

import ManualAccrualDetailDialog from './ManualAccrualDetailDialog';
import RetryManualAccrualsDialog from './RetryManualAccrualsDialog';
import ManualAccrualsSearchFiltersSection from './components/ManualAccrualsSearchFiltersSection';
import RowPinningSection from './components/RowPinningSection';

interface CustomToolbarProps {
  totalCount: number;
}

// Custom toolbar component
function CustomToolbar({ totalCount }: CustomToolbarProps) {
  const theme = useTheme();
  return (
    <GridToolbarContainer
      sx={{
        padding: 2,
        gap: 2,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(10px)',
      }}
    >
      <GridToolbarColumnsButton
        slotProps={{
          button: {
            sx: {
              borderRadius: 2,
              textTransform: 'none',
            },
          },
        }}
      />
      <GridToolbarFilterButton
        slotProps={{
          button: {
            sx: {
              borderRadius: 2,
              textTransform: 'none',
            },
          },
        }}
      />
      <GridToolbarExport
        slotProps={{
          button: {
            sx: {
              borderRadius: 2,
              textTransform: 'none',
            },
          },
        }}
      />
      <Typography variant="body2" color="text.primary">
        {totalCount} Total
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter
        debounceMs={500}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(10px)',
          },
        }}
      />
    </GridToolbarContainer>
  );
}

export default function LoyaltyManualAccrualsPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();
  const { search } = useLocation();

  // Theme color for styling
  const themeColor = theme.palette.primary.main;

  // Read pre-fill values from URL query params (e.g. ?orderId=X&itemId=Y)
  const urlParams = new URLSearchParams(search);
  const prefilledOrderId = urlParams.get('orderId') ?? '';
  const prefilledItemId = urlParams.get('itemId') ?? '';

  // Pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Filter input state — pre-filled from URL params when present
  const [customerIdInput, setCustomerIdInput] = useState('');
  const [orderIdInput, setOrderIdInput] = useState(prefilledOrderId);
  const [itemIdInput, setItemIdInput] = useState(prefilledItemId);
  const [selectedProductType, setSelectedProductType] = useState<ProductType | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<ManualAccrualStatus | 'all'>('PENDING');

  // Applied filters — auto-apply URL params on initial load if present
  const [appliedFilters, setAppliedFilters] = useState<ManualAccrualFilterParams>({
    status: 'PENDING',
    ...(prefilledOrderId ? { orderId: prefilledOrderId } : {}),
    ...(prefilledItemId ? { itemId: prefilledItemId } : {}),
  });

  // Dialog state
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedAccrualForDetail, setSelectedAccrualForDetail] = useState<ManualAccrual | null>(null);

  // Row selection state
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // Pinned rows state (session only, not persisted)
  const [pinnedRows, setPinnedRows] = useState<{
    top: Array<ManualAccrual>;
    bottom: Array<ManualAccrual>;
  }>({ top: [], bottom: [] });

  // Build query params for API
  const queryParams: ManualAccrualFilterParams = useMemo(
    () => ({
      page: paginationModel.page + 1, // API uses 1-based indexing
      pageSize: paginationModel.pageSize,
      ...appliedFilters,
    }),
    [paginationModel, appliedFilters],
  );

  // Query for fetching manual accruals
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['loyalty', 'manual-accruals', queryParams],
    queryFn: async ({ signal }) => {
      return await getManualAccruals(queryParams, { signal });
    },
    placeholderData: (previousData) => previousData,
    retry: false,
  });

  const pageInfo = data?.pageInfo;
  const totalCount: number =
    pageInfo?.totalRecords ?? (pageInfo?.pageCount ? pageInfo.pageCount * paginationModel.pageSize : 0);
  const accruals = useMemo(() => data?.result ?? [], [data?.result]);

  // Filter out pinned rows from the main grid
  const unpinnedRows = useMemo(() => {
    const allPinnedIds = [...pinnedRows.top.map((r) => r.id), ...pinnedRows.bottom.map((r) => r.id)];
    return accruals.filter((row) => !allPinnedIds.includes(row.id));
  }, [accruals, pinnedRows]);

  const gridPinnedRows: GridPinnedRowsProp = useMemo(
    () => ({
      top: pinnedRows.top,
      bottom: pinnedRows.bottom,
    }),
    [pinnedRows],
  );

  const handleSearch = useCallback((): void => {
    setAppliedFilters({
      customerId: customerIdInput.trim() || undefined,
      orderId: orderIdInput.trim() || undefined,
      itemId: itemIdInput.trim() || undefined,
      productType: selectedProductType || undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [customerIdInput, orderIdInput, itemIdInput, selectedProductType, selectedStatus]);

  const handleClearFilters = useCallback((): void => {
    setCustomerIdInput('');
    setOrderIdInput('');
    setItemIdInput('');
    setSelectedProductType('');
    setSelectedStatus('all');
    setAppliedFilters({});
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  const handleRetryDialogClose = useCallback(() => {
    setIsRetryDialogOpen(false);
  }, []);

  const handleRetrySuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['loyalty', 'manual-accruals'] });
    setRowSelectionModel([]);
    refetch();
  }, [queryClient, refetch]);

  const handleRetrySelected = useCallback(() => {
    if (rowSelectionModel.length > 0) {
      setIsRetryDialogOpen(true);
    }
  }, [rowSelectionModel]);

  const handleViewDetails = useCallback((accrual: ManualAccrual) => {
    setSelectedAccrualForDetail(accrual);
    setIsDetailDialogOpen(true);
  }, []);

  const handleDetailDialogClose = useCallback(() => {
    setIsDetailDialogOpen(false);
    setSelectedAccrualForDetail(null);
  }, []);

  const handleRetryFromDetail = useCallback(() => {
    if (selectedAccrualForDetail) {
      setIsDetailDialogOpen(false);
      setRowSelectionModel([selectedAccrualForDetail.id]);
      setIsRetryDialogOpen(true);
    }
  }, [selectedAccrualForDetail]);

  // Get selected accrual objects from IDs
  const selectedAccruals = useMemo(() => {
    const allRows = [...accruals, ...pinnedRows.top, ...pinnedRows.bottom];
    return allRows.filter((accrual) => (rowSelectionModel as Array<string>).includes(accrual.id));
  }, [accruals, pinnedRows, rowSelectionModel]);

  // Row pinning handlers
  const handlePinToTop = useCallback(() => {
    const selectedIds = rowSelectionModel as Array<string>;
    const selectedRows = accruals.filter((row) => selectedIds.includes(row.id));

    setPinnedRows((prev) => {
      const newBottom = prev.bottom.filter((row) => !selectedIds.includes(row.id));
      const existingTopIds = new Set(prev.top.map((r) => r.id));
      const newTop = [...prev.top, ...selectedRows.filter((r) => !existingTopIds.has(r.id))];
      return { top: newTop, bottom: newBottom };
    });
    setRowSelectionModel([]);
  }, [rowSelectionModel, accruals]);

  const handlePinToBottom = useCallback(() => {
    const selectedIds = rowSelectionModel as Array<string>;
    const selectedRows = accruals.filter((row) => selectedIds.includes(row.id));

    setPinnedRows((prev) => {
      const newTop = prev.top.filter((row) => !selectedIds.includes(row.id));
      const existingBottomIds = new Set(prev.bottom.map((r) => r.id));
      const newBottom = [...prev.bottom, ...selectedRows.filter((r) => !existingBottomIds.has(r.id))];
      return { top: newTop, bottom: newBottom };
    });
    setRowSelectionModel([]);
  }, [rowSelectionModel, accruals]);

  const handleUnpinAll = useCallback(() => {
    setPinnedRows({ top: [], bottom: [] });
  }, []);

  const handleUnpinRow = useCallback((rowId: string) => {
    setPinnedRows((prev) => ({
      top: prev.top.filter((row) => row.id !== rowId),
      bottom: prev.bottom.filter((row) => row.id !== rowId),
    }));
  }, []);

  const isRowPinned = useCallback(
    (rowId: string) => {
      return pinnedRows.top.some((r) => r.id === rowId) || pinnedRows.bottom.some((r) => r.id === rowId);
    },
    [pinnedRows],
  );

  const getStatusColor = (status: ManualAccrualStatus): 'warning' | 'success' | 'error' => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'PROCESSED':
        return 'success';
      case 'FAILED':
        return 'error';
      default:
        return 'warning';
    }
  };

  const columns: Array<GridColDef<ManualAccrual>> = useMemo(
    () => [
      {
        field: 'manualAccrualStatus',
        headerName: 'Status',
        width: 120,
        renderCell: ({ value }) => <Chip label={value} color={getStatusColor(value)} size="small" />,
      },
      {
        field: 'details',
        headerName: 'Details',
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Tooltip title="View details">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(row);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: 'customerId',
        headerName: 'Customer ID',
        width: 150,
        renderCell: ({ value }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Box>
            <Tooltip title="Open customer">
              <IconButton
                component={Link}
                to={`/users/${value}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'orderId',
        headerName: 'Order ID',
        width: 150,
        renderCell: ({ value }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Box>
            <Tooltip title="Open order">
              <IconButton
                component={Link}
                to={`/purchases/${value}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'itemId',
        headerName: 'Item ID',
        width: 150,
        renderCell: ({ value, row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Box>
            <Tooltip title="Open order">
              <IconButton
                component={Link}
                to={`/purchases/${row.orderId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        width: 150,
      },
      {
        field: 'ratePlanId',
        headerName: 'Rate Plan ID',
        width: 150,
        renderCell: ({ value }) => value || '-',
      },
      {
        field: 'pointsFromEstimate',
        headerName: 'Est. Points',
        width: 110,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => {
          const total = row.pointsFromEstimate + row.bonusPointsFromEstimate;
          return total;
        },
      },
      {
        field: 'pendingPoints.totalPoints',
        headerName: 'Pending Points',
        width: 130,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value, row) => {
          if (!row.pendingPoints) {
            return null;
          }
          return row.pendingPoints.points + row.pendingPoints.bonusPoints;
        },
        renderCell: ({ value }) => (value === null || value === undefined ? '-' : value),
      },
      {
        field: 'errorMessage',
        headerName: 'Error',
        width: 200,
        flex: 1,
        display: 'flex',
        renderCell: ({ value }) => {
          if (!value) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Tooltip title={value}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {value}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 110,
        valueFormatter: (value) => {
          if (!value) {
            return '';
          }
          return dayjs(value).format('DD/MM/YY');
        },
      },
      {
        field: 'pinAction',
        headerName: '',
        width: 50,
        minWidth: 50,
        editable: false,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        disableExport: true,
        renderCell: ({ row }) => {
          const isPinned = isRowPinned(row.id);
          if (!isPinned) {
            return null;
          }
          return (
            <Tooltip title="Click to unpin row">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnpinRow(row.id);
                }}
                sx={{
                  color: theme.palette.warning.main,
                  '&:hover': {
                    color: theme.palette.warning.dark,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                  },
                }}
              >
                <PinIcon sx={{ fontSize: theme.typography.body2.fontSize }} />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [theme, isRowPinned, handleUnpinRow, handleViewDetails],
  );

  return (
    <Box sx={{ p: 1 }}>
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 1.25,
          mb: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                lineHeight: 1.2,
                background: `linear-gradient(135deg, ${themeColor}, ${alpha(themeColor, 0.7)})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Manual Points Accruals
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Manage and retry manual loyalty point accruals
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleRetrySelected}
            disabled={rowSelectionModel.length === 0}
            sx={{
              bgcolor: themeColor,
              textTransform: 'none',
              '&:hover': {
                bgcolor: alpha(themeColor, 0.8),
              },
            }}
          >
            Retry Selected ({rowSelectionModel.length})
          </Button>
        </Box>
      </Paper>

      {/* Search Filters */}
      <ManualAccrualsSearchFiltersSection
        themeColor={themeColor}
        customerIdInput={customerIdInput}
        orderIdInput={orderIdInput}
        itemIdInput={itemIdInput}
        selectedProductType={selectedProductType}
        selectedStatus={selectedStatus}
        setCustomerIdInput={setCustomerIdInput}
        setOrderIdInput={setOrderIdInput}
        setItemIdInput={setItemIdInput}
        setSelectedProductType={setSelectedProductType}
        setSelectedStatus={setSelectedStatus}
        onSearch={handleSearch}
        onClear={handleClearFilters}
      />

      {/* Row Pinning Controls */}
      <RowPinningSection
        selectedRowCount={rowSelectionModel.length}
        pinnedTopCount={pinnedRows.top.length}
        pinnedBottomCount={pinnedRows.bottom.length}
        onPinToTop={handlePinToTop}
        onPinToBottom={handlePinToBottom}
        onUnpinAll={handleUnpinAll}
        themeColor={themeColor}
      />

      {error && (
        <Box mb={3}>
          <Alert severity="error">
            Error loading manual accruals: {error instanceof Error ? error.message : String(error)}
          </Alert>
        </Box>
      )}

      {/* Data Grid */}
      <Paper
        elevation={0}
        sx={{
          height: 650,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          rows={unpinnedRows}
          columns={columns}
          loading={isLoading}
          pinnedRows={gridPinnedRows}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          isRowSelectable={(params) => !isRowPinned(params.id as string)}
          slots={{
            toolbar: (props: GridToolbarProps) => <CustomToolbar totalCount={totalCount} {...props} />,
          }}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          density="comfortable"
          sx={{
            height: '100%',
            '& .MuiDataGrid-row:hover': {
              bgcolor: alpha(themeColor, 0.08),
            },
            '& .MuiDataGrid-pinnedRows--top': {
              borderBottom: `3px solid ${alpha(themeColor, 0.3)}`,
              bgcolor: theme.palette.background.paper,
            },
            '& .MuiDataGrid-pinnedRows--bottom': {
              borderTop: `3px solid ${alpha(themeColor, 0.3)}`,
              bgcolor: theme.palette.background.paper,
            },
          }}
        />
      </Paper>

      {/* Dialogs */}
      <ManualAccrualDetailDialog
        open={isDetailDialogOpen}
        onClose={handleDetailDialogClose}
        accrual={selectedAccrualForDetail}
        onRetry={handleRetryFromDetail}
      />

      <RetryManualAccrualsDialog
        open={isRetryDialogOpen}
        onClose={handleRetryDialogClose}
        selectedAccruals={selectedAccruals}
        onSuccess={handleRetrySuccess}
      />
    </Box>
  );
}
